import { classes } from 'html-classes';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import CloudflareResponseImage from '~/components/CloudflareResponsiveImage/CloudflareResponseImage';
import { defaultSrcSetParams } from '~/components/CloudflareResponsiveImage/constants';
import { useEmblaCarouselAutoplay } from '~/components/isolated/HeroBanner/hooks';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';
import { useGlobal } from '~/hooks/useGlobal';
import { userStore } from '~/stores';
import type { Category } from '~/stores/CategoriesStore';

import CarouselIcon from '../../CommonCarousel/CarouselIcon';
import { createCategoryUrl } from '../utils';
import styles from './ListOfCategoriesDE.module.scss';

interface ListOfCategoriesDEProps {
  category: Category;
}

type Subcategory = Category['subcategory'][number];

const ListOfCategoriesDE = ({ category }: ListOfCategoriesDEProps) => {
  const { handleClickCategory } = useCategoryAnalytics();
  const { isMobile } = useGlobal();
  const { t } = useTranslation();

  const slides = useMemo<Subcategory[][]>(() => {
    if (!category.subcategory.length || !category.subcategory[0]?.id) {
      return [];
    }

    if (!isMobile) {
      return category.subcategory.map((item) => [item]);
    }

    const chunkSize = 6; // 2 rows * 3 columns
    const result: Subcategory[][] = [];

    for (let i = 0; i < category.subcategory.length; i += chunkSize) {
      result.push(category.subcategory.slice(i, i + chunkSize));
    }

    return result;
  }, [category.subcategory, isMobile]);

  const slidesCount = slides.length;

  const {
    emblaRef,
    selectedIndex,
    animationKey,
    scrollPrev,
    scrollNext,
    scrollTo,
    canScrollPrev,
    canScrollNext,
  } = useEmblaCarouselAutoplay(slidesCount, {
    direction: userStore.dir,
    loop: isMobile,
  });

  if (!slidesCount) {
    return null;
  }

  const shouldShowPagination = isMobile && slidesCount > 1;
  return (
    <div className={styles.container}>
      <div className={classes([styles.content, 'banner-carousel carousel'])}>
        {!isMobile && canScrollPrev && (
          <CarouselIcon
            position="left"
            variant="fill"
            className={styles.arrow}
            onClick={scrollPrev}
            iconSize={18}
          />
        )}
        <div className={styles.viewport} ref={emblaRef} key={userStore.dir}>
          <div className={styles.track} dir={userStore.dir}>
            {slides.map((slideGroup, slideIndex) => (
              <div
                className={classes([
                  styles.slide,
                  isMobile && styles.slideMobile,
                ])}
                key={slideGroup.map((item) => item.id).join('-') || slideIndex}
              >
                {isMobile ? (
                  <div className={styles.grid}>
                    {slideGroup.map(({ name, previewImage, id }) => (
                      <div key={id} className={styles.item}>
                        <Link
                          data-link={`category-${id}`}
                          onClick={handleClickCategory(id)}
                          to={createCategoryUrl({ id })}
                          className={styles.link}
                        >
                          <div className={styles.slideTitle}>{name}</div>

                          <CloudflareResponseImage
                            src={previewImage || ''}
                            srcParams={{ width: 300, height: 300 }}
                            srcSetParams={defaultSrcSetParams}
                            alt=""
                            className={styles.slidePreviewImage}
                            loading="lazy"
                          />
                        </Link>
                      </div>
                    ))}
                  </div>
                ) : (
                  slideGroup.map(
                    ({ name, previewImage, id, productsCount }) => (
                      <div key={id} className={styles.item}>
                        <Link
                          data-link={`category-${id}`}
                          onClick={handleClickCategory(id)}
                          to={createCategoryUrl({ id })}
                          className={styles.link}
                        >
                          <div className={styles.slideTitle}>{name}</div>
                          <div className={styles.slideProductsCount}>
                            {t('phrases:itemsCount', { count: productsCount })}
                          </div>
                          <CloudflareResponseImage
                            src={previewImage || ''}
                            srcParams={{ width: 300, height: 300 }}
                            srcSetParams={defaultSrcSetParams}
                            alt=""
                            className={styles.slidePreviewImage}
                            loading="lazy"
                          />
                        </Link>
                      </div>
                    ),
                  )
                )}
              </div>
            ))}
          </div>
        </div>
        {!isMobile && canScrollNext && (
          <CarouselIcon
            className={styles.arrow}
            onClick={scrollNext}
            position="right"
            variant="fill"
            iconSize={18}
          />
        )}
      </div>
      {shouldShowPagination && (
        <div className={styles.pagination}>
          {slides.map((slideGroup, index) => (
            <button
              key={slideGroup.map((item) => item.id).join('-') || index}
              type="button"
              className={classes([
                styles.dot,
                index === selectedIndex && styles.dotActive,
              ])}
              onClick={() => scrollTo(index)}
            >
              {index === selectedIndex && (
                <span
                  key={`${selectedIndex}-${animationKey}`}
                  className={styles.dotProgress}
                />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ListOfCategoriesDE;
